import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { resolveImage, formatRM } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { usePageTitle } from "@/contexts/PageTitlesContext";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, ShoppingBag, Check } from "lucide-react";

export default function Events() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const pt = usePageTitle("events", { title_zh: t("events.title"), title_en: t("events.title"), subtitle_zh: t("events.subtitle"), subtitle_en: t("events.subtitle") });
  const [events, setEvents] = useState([]);
  const { addEvent, eventItems } = useCart();

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const inCart = (id) => eventItems.some((i) => i.event_id === id);

  const onAdd = (ev) => {
    addEvent(ev, 1);
    toast.success(t("events2.addedToCart"));
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="events-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold" data-testid="events-subtitle">
        {zh ? pt.subtitle_zh : pt.subtitle_en}
      </div>
      <h1 className="mt-3 font-serif-cn text-4xl md:text-5xl text-[#003645] font-semibold tracking-tight" data-testid="events-title">
        {zh ? pt.title_zh : pt.title_en}
      </h1>

      <div className="mt-12 grid md:grid-cols-2 gap-8" data-testid="events-list">
        {events.length === 0 ? (
          <div className="text-[#4a6e77]">{t("events.empty")}</div>
        ) : events.map((ev) => (
          <div key={ev.id} className="rounded-lg border border-[#e5ebeb] bg-white overflow-hidden" data-testid={`event-card-${ev.id}`}>
            {ev.image && <div className="aspect-[16/9] bg-[#f5f0e8]"><img src={resolveImage(ev.image)} alt="" className="h-full w-full object-cover" /></div>}
            <div className="p-6">
              <h3 className="font-serif-cn text-xl text-[#003645] font-semibold">
                {zh ? ev.title_zh : ev.title_en}
              </h3>
              <p className="mt-3 text-sm text-[#4a6e77] leading-relaxed">
                {zh ? ev.desc_zh : ev.desc_en}
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-[#4a6e77]">
                <li className="flex items-center gap-2"><CalendarDays size={14} /> {new Date(ev.starts_at).toLocaleString()}</li>
                <li className="flex items-center gap-2"><MapPin size={14} /> {zh ? ev.location_zh : ev.location_en}</li>
                <li className="flex items-center gap-2"><Users size={14} /> {t("events.capacity")}: {ev.capacity}</li>
              </ul>
              <div className="mt-5 flex items-center justify-between">
                <div className="text-[#005870] font-semibold">{formatRM(ev.price)}</div>
                {inCart(ev.id) ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#00868e]/10 text-[#00868e] text-sm font-medium"
                    data-testid={`event-in-cart-${ev.id}`}
                  >
                    <Check size={14} /> {t("events2.eventInCart")}
                  </span>
                ) : (
                  <button
                    onClick={() => onAdd(ev)}
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] transition-colors duration-200"
                    data-testid={`event-add-cart-${ev.id}`}
                  >
                    <ShoppingBag size={14} /> {t("events2.addToCart")}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
